///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2021, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2021 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

/**
 * Each event is represented by an object and may have additional custom fields to provide
 * information about what happened.
 */
export interface Event {
  /**
   * Event type.
   */
  type: string;

  /**
   * Additional custom fields. See events description for more information.
   */
  [key: string]: any;
}

/**
 * Event listener function.
 *
 * @param event - Event object.
 */
export interface EventListener {
  (event: Event): void;
}

export class EventEmitter2 {
  private _listeners: EventListener[] = [];

  addEventListener(type: string, listener: EventListener): this {
    if (!this._listeners[type]) {
      this._listeners[type] = [];
    }

    this._listeners[type].push(listener);
    return this;
  }

  removeEventListener(type: string, listener: EventListener): this {
    if (!this._listeners[type]) {
      return this;
    }

    this._listeners[type] = this._listeners[type].filter((x) => x !== listener);
    return this;
  }

  removeAllListeners(type?: string): this {
    if (type) {
      this._listeners[type] = [];
    } else {
      this._listeners = [];
    }

    return this;
  }

  emitEvent(event: Event): boolean {
    if (!this._listeners[event.type]) {
      return false;
    }

    const invoke = [...this._listeners[event.type]];
    invoke.forEach((listener) => listener.call(this, event));
    return true;
  }

  // for compatibility with Node.js EventEmitter

  on(type: string, listener: EventListener): this {
    return this.addEventListener(type, listener);
  }

  off(type: string, listener: EventListener): this {
    return this.removeEventListener(type, listener);
  }

  emit(event: string | object, ...args: any[]): boolean {
    if (typeof event === "string") return this.emit({ type: event, ...args });
    else if (typeof event === "object") return this.emitEvent(event as Event);
    else return false;
  }
}
